import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'shared/utils/dayjsConfig';

import { IState } from 'shared/interfaces/state.interface';
import {
  TConsultationQueueRecord,
  TConsultationsQueueOrder,
  TConsultationsQueueResponse,
  TFilterOptions
} from 'shared/types';
import { initialStore } from '../initial-store';

type TConsultationsQueueState = {
  isLoading: boolean;
  records: TConsultationQueueRecord[];
  countRecords: number;
};

const consultationsQueueInitialState: TConsultationsQueueState = {
  ...initialStore.consultationsQueue,
  records: initialStore.consultationsQueue.records as TConsultationQueueRecord[]
};

const getProductsTitle = (
  productVariations: TConsultationsQueueOrder['productVariations']
) => {
  if (!Array.isArray(productVariations) || !productVariations.length) {
    return '';
  }

  return productVariations
    .map((item) => item.description || item.id)
    .join(', ');
};

const getFullName = (
  person?: { firstName?: string; lastName?: string } | null
) =>
  person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '';

const consultationsQueueSlice = createSlice({
  name: 'consultationsQueue',
  initialState: consultationsQueueInitialState,
  reducers: {
    getActiveConsultations: (
      state,
      data: PayloadAction<TFilterOptions | undefined>
    ) => {
      if (data.payload?.page === 1) {
        state.records = [];
      }

      if (!state.isLoading) {
        state.isLoading = true;
      }
    },
    setActiveConsultations: (
      state,
      { payload }: PayloadAction<TConsultationsQueueResponse>
    ) => {
      state.isLoading = false;
      const records: TConsultationQueueRecord[] = payload.orders.reduce(
        (acc: TConsultationQueueRecord[], item: TConsultationsQueueOrder) => {
          const patient = getFullName(item.patient);
          const products = getProductsTitle(item.productVariations);
          const difference = dayjs().diff(dayjs(item.createdAt));
          const providerName = getFullName(item.practitioner);
          const provider = providerName ? `Dr. ${providerName}` : '';

          return [
            ...acc,
            {
              patient,
              products,
              difference,
              provider
            }
          ];
        },
        []
      );
      state.records = [...state.records, ...records];
      state.countRecords = payload.countOrders;
    },
    getActiveConsultationsFail: (state) => {
      state.isLoading = false;
    }
  }
});

export const {
  getActiveConsultations,
  setActiveConsultations,
  getActiveConsultationsFail
} = consultationsQueueSlice.actions;

export const { reducer } = consultationsQueueSlice;

// Selectors
export const getActiveConsultationsLoadingSelector = (state: IState) =>
  state && state.consultationsQueue.isLoading;

export const getConsultationsRecordSelector = (state: IState) =>
  state && state.consultationsQueue.records;

export const getConsultationsRecordCountSelector = (state: IState) =>
  state && state.consultationsQueue.countRecords;
